import LoadingButton from '@mui/lab/LoadingButton'
import {Box, Button, Chip, TextField} from '@mui/material'
import type {FC, FormEvent, ReactElement} from 'react'
import {useEffect, useMemo, useState} from 'react'
import EntryTimeChip, {resolveTimeChoice} from './EntryTimeChip'
import type {TimeChoice, TimeRange} from './EntryTimeChip'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import Picker from '@shared/components/base/inputs/Picker'
import {useLogbookCategoryOptions} from '@shared/components/logbook/useLogbookCategoryOptions'
import {withUnlistedSelection} from '@shared/hooks/useSeenOptionNames'

/** How many categories show as one-tap chips before the rest move to "More". */
const CATEGORY_CHIPS = 5

export type ComposerStatus = {canSave: boolean; saving: boolean}

export type ComposerValues = {
  text: string
  categoryId: string | null
  tStart: string
  tEnd: string
}

interface EntryComposerProps {
  initialText?: string
  initialCategory?: {id: string; name: string} | null
  initialTime: TimeChoice
  batchRange: TimeRange | null
  placeholder: string
  submitLabel: string
  /** Resolves true when saved; the composer then clears itself if `resetOnSave`. */
  onSubmit: (values: ComposerValues) => Promise<boolean>
  onCancel?: () => void
  resetOnSave?: boolean
  autoFocus?: boolean
  dataCy: string
  /**
   * Inside a modal, the Save button belongs in the modal's footer like every
   * other modal's: give the composer a form id, render `ComposerSaveButton`
   * with the same id in the footer, and the composer drops its own buttons.
   */
  formId?: string
  onStatusChange?: (status: ComposerStatus) => void
}

/**
 * Writes one logbook entry: the text first, then when it happened and an
 * optional category as chips. Used for a new entry, an edit and a correction.
 */
const EntryComposer: FC<EntryComposerProps> = ({
  initialText = '',
  initialCategory = null,
  initialTime,
  batchRange,
  placeholder,
  submitLabel,
  onSubmit,
  onCancel,
  resetOnSave = false,
  autoFocus = false,
  dataCy,
  formId,
  onStatusChange,
}): ReactElement => {
  const [t] = useCustomTranslation('logbook')
  const [tCommon] = useCustomTranslation('common')
  const categories = useLogbookCategoryOptions()
  const [text, setText] = useState(initialText)
  const [categoryId, setCategoryId] = useState<string | null>(initialCategory?.id ?? null)
  const [time, setTime] = useState<TimeChoice>(initialTime)
  const [saving, setSaving] = useState(false)

  // A batch clicked on the timeline while the panel is open becomes the new
  // default — unless the user already picked a time themselves.
  const batchKey = batchRange ? `${batchRange.start}|${batchRange.end}` : ''
  useEffect(() => {
    setTime((current) =>
      current.kind === 'custom'
        ? current
        : batchRange
          ? {kind: 'batch', range: batchRange}
          : current,
    )
  }, [batchKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const chipOptions = categories.options.slice(0, CATEGORY_CHIPS)
  const hasMoreCategories = categories.options.length > CATEGORY_CHIPS || categories.hasMore
  // A selection outside the chips (picked under "More", or the entry's own)
  // still shows as a selected chip.
  const selectedOutsideChips =
    categoryId && !chipOptions.some((option) => option.id === categoryId)
      ? (categories.options.find((option) => option.id === categoryId) ??
        (initialCategory?.id === categoryId ? initialCategory : {id: categoryId, name: categoryId}))
      : null
  const moreOptions = useMemo(
    () =>
      withUnlistedSelection(
        categories.options,
        categoryId ? [categoryId] : [],
        initialCategory ? {[initialCategory.id]: initialCategory.name} : {},
      ),
    [categories.options, categoryId, initialCategory],
  )

  const timeRange = time.kind === 'now' ? null : time.range
  const isTimeValid =
    !timeRange || new Date(timeRange.start).getTime() <= new Date(timeRange.end).getTime()
  const canSave = !!text.trim() && isTimeValid && !saving

  useEffect(() => {
    onStatusChange?.({canSave, saving})
  }, [canSave, saving, onStatusChange])

  const save = async (): Promise<void> => {
    if (!canSave) {
      return
    }
    setSaving(true)
    const range = resolveTimeChoice(time)
    const saved = await onSubmit({
      text: text.trim(),
      categoryId,
      tStart: range.start,
      tEnd: range.end,
    })
    setSaving(false)
    if (saved && resetOnSave) {
      setText('')
      setCategoryId(null)
      setTime(batchRange ? {kind: 'batch', range: batchRange} : {kind: 'now'})
    }
  }

  const toggleCategory = (id: string): void =>
    setCategoryId((current) => (current === id ? null : id))

  return (
    <Box
      component={formId ? 'form' : 'div'}
      id={formId}
      onSubmit={(event: FormEvent) => {
        event.preventDefault()
        void save()
      }}
      data-cy={dataCy}
      data-testid={dataCy}
      sx={{
        border: 1,
        borderColor: 'primary.main',
        borderRadius: 1,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <TextField
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={placeholder}
        multiline
        minRows={2}
        fullWidth
        autoFocus={autoFocus}
        variant="standard"
        slotProps={{input: {disableUnderline: true}, htmlInput: {'data-cy': `${dataCy}-text`}}}
      />
      {(chipOptions.length > 0 || hasMoreCategories) && (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center'}}>
          {chipOptions.map((option) => (
            <Chip
              key={option.id}
              size="small"
              label={option.name}
              color={categoryId === option.id ? 'primary' : 'default'}
              variant={categoryId === option.id ? 'filled' : 'outlined'}
              onClick={() => toggleCategory(option.id)}
              data-cy={`${dataCy}-category`}
            />
          ))}
          {selectedOutsideChips && (
            <Chip
              size="small"
              color="primary"
              label={selectedOutsideChips.name}
              onDelete={() => setCategoryId(null)}
            />
          )}
          {hasMoreCategories && (
            <Picker
              {...categories}
              options={moreOptions}
              label={t('more_categories')}
              placeholder={t('category')}
              value={categoryId}
              onChange={(id) => setCategoryId(id || null)}
              size="small"
              minWidth="0"
              dataCy={`${dataCy}-more-categories`}
            />
          )}
        </Box>
      )}
      {/* One row: when it happened on the left, the actions on the right. The
          custom start/end pickers, when open, wrap below the whole row. */}
      <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1}}>
        <EntryTimeChip value={time} onChange={setTime} batchRange={batchRange} />
        <Box sx={{flex: 1, order: 1}} />
        {/* In a modal the footer holds Save (see ComposerSaveButton). */}
        {!formId && (
          <Box sx={{display: 'flex', gap: 1, order: 2}}>
            {onCancel && (
              <Button size="small" onClick={onCancel}>
                {tCommon('cancel')}
              </Button>
            )}
            <LoadingButton
              size="small"
              variant="contained"
              loading={saving}
              disabled={!canSave}
              onClick={() => void save()}
              data-cy={`${dataCy}-save`}
            >
              {submitLabel}
            </LoadingButton>
          </Box>
        )}
      </Box>
    </Box>
  )
}

/** The Save button of a composer that lives in a modal, for the modal's footer. */
export const ComposerSaveButton: FC<{
  formId: string
  status: ComposerStatus
  label: string
  dataCy: string
}> = ({formId, status, label, dataCy}): ReactElement => (
  <LoadingButton
    type="submit"
    form={formId}
    variant="contained"
    loading={status.saving}
    disabled={!status.canSave}
    data-cy={dataCy}
  >
    {label}
  </LoadingButton>
)

export default EntryComposer
